/**
 * SocialGroupManager — Phase 27.1 (v5) implementation.
 *
 * Design doc: docs/alds-roadmap-v5-phase27.md
 *
 * Manages ALDS Dedicated Social Communications Surfaces (ADSCS). A social
 * group is a metadata wrapper over an existing MLS thread:
 *
 *   social_group (visibility, presentation style, moderation, federation)
 *      ↓ wraps
 *   ThreadManager group (MLS-encrypted, signed envelopes)
 *      ↓ writes to
 *   message_cache  (per-peer replicated row stream)
 *
 * The 5 presentation styles (feed / timeline / gallery / broadcast /
 * channel) are renderer choices — they do NOT change the underlying
 * encrypted data. Users can override a group's default style in their
 * own client (`SocialSubscriptionRecord.presentationOverride`).
 *
 * Phase 27.1 scope:
 *   - createGroup / getGroup / listGroups / archiveGroup / updateGroup
 *   - joinGroup / leaveGroup / listMembers / setNotifyPreference /
 *     setPresentationOverride
 *   - publish / listMessages / markRead / listMyMentions
 *
 * Deferred to 27.2+:
 *   - editPost, react, votePoll, hidePost
 *   - sealed-sender mode, semaphore-anonymous mode
 *   - search
 *   - federateGroup, unfederateGroup
 *
 * Threading model: every public method audits under category 'social' on
 * success. Failures throw and are NOT audited (the audit log records
 * applied state transitions only).
 */

import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import { ThreadManager, type MessageCacheRow } from '../threads/ThreadManager';
import type {
  AuditLogRecord,
  MessageCacheRecord,
  MessageKind,
  SocialGroupKind,
  SocialGroupRecord,
  SocialHidePayload,
  SocialHideVotePayload,
  SocialMentionRecord,
  SocialModerationPolicy,
  SocialPollVotePayload,
  SocialPostPayload,
  SocialPresentationStyle,
  SocialRestorePayload,
  SocialSenderModel,
  SocialSubscriptionRecord,
  SocialVisibility,
} from '../storage/schema';

// ─── Errors ────────────────────────────────────────────────────────────────

export class NotImplementedInPhase27 extends Error {
  constructor(method: string) {
    super(`SocialGroupManager.${method}: requires Phase 27.2+. Sketched only.`);
    this.name = 'NotImplementedInPhase27';
  }
}

export class SocialAuthError extends Error {
  constructor(
    public readonly reason:
      | 'not-a-member'
      | 'not-a-moderator'
      | 'group-archived'
      | 'visibility-mismatch'
      | 'only-author-may-withdraw',
  ) {
    super(`SocialGroupManager: ${reason}`);
    this.name = 'SocialAuthError';
  }
}

export class SocialValidationError extends Error {
  constructor(message: string) {
    super(`SocialGroupManager: ${message}`);
    this.name = 'SocialValidationError';
  }
}

// ─── Option types ──────────────────────────────────────────────────────────

export interface CreateGroupOptions {
  name: string;
  kind: SocialGroupKind;
  visibility: SocialVisibility;
  /** Defaults to `kind` (e.g. kind=channel → presentationStyle=channel). */
  presentationStyle?: SocialPresentationStyle;
  senderModel?: SocialSenderModel;
  defaultTtlSec?: number;
  moderation?: SocialModerationPolicy;
  /** Initial moderator pubkeys (other than creator). */
  moderatorPubkeys?: string[];
  /** Federation link ids to attach this group to. */
  federationLinkIds?: string[];
  description?: string;
}

export interface PublishOptions {
  groupId: string;
  text?: string;
  mediaManifestIds?: string[];
  linkCardUrl?: string;
  parentHash?: string;
  tags?: string[];
  mentionPubkeys?: string[];
  pollOptions?: string[];
  /** Override the group default TTL for this single post. */
  ttlSec?: number;
  /** Ephemeral story flag — auto-expires + only renders in social-stories. */
  story?: boolean;
  /**
   * Phase 46 (v9): when present, the blob is stored in the Vault before
   * publishing the post, and the resulting manifest id is appended to
   * `mediaManifestIds`. `voiceNoteMimeType` defaults to `audio/webm`.
   */
  voiceNoteBlob?: Blob;
  voiceNoteMimeType?: string;
  voiceNoteFilename?: string;
}

export interface PublishResult {
  messageHash: string;
  envelopeHash: string;
  publishedAt: string;
}

export interface ListMessagesOptions {
  groupId: string;
  /** Newest-first by default; flip via `order: 'asc'`. */
  order?: 'asc' | 'desc';
  /** Restrict to top-level posts (default) vs include all (replies inline). */
  kindFilter?: 'posts-only' | 'all';
  /** Cursor — message hash to paginate from. */
  before?: string;
  /** Page size (default 50). */
  limit?: number;
  /** When true, populate `replyCount` on each returned ListedMessage. */
  includeReplyCount?: boolean;
  /**
   * Whether to include `story: true` posts. Default false — stories
   * appear in the `social-stories` surface only.
   */
  includeStories?: boolean;
  /**
   * Whether to include posts that have been hidden by moderators or
   * community-vote. Default false. When true, returned messages still
   * carry `hidden: true` so callers can render a placeholder.
   */
  includeHidden?: boolean;
}

export interface ListRepliesOptions {
  groupId: string;
  parentHash: string;
  /** Default ascending (oldest first — threaded conversation order). */
  order?: 'asc' | 'desc';
  limit?: number;
  /** Default false. See ListMessagesOptions.includeHidden. */
  includeHidden?: boolean;
}

export interface ListedMessage {
  messageHash: string;
  senderPubkey: string;
  sentAt: string;
  payload: SocialPostPayload;
  /** Populated only when ListMessagesOptions.includeReplyCount is true. */
  replyCount?: number;
  /**
   * Populated whenever hidden filtering is in scope (either default or
   * `includeHidden: true`). Lets UIs render a "hidden by moderator"
   * placeholder without an extra round-trip to `isHidden`.
   */
  hidden?: boolean;
}

export interface SearchOptions {
  query: string;
  groupId?: string;
  limit?: number;
}

export interface SearchHit {
  groupId: string;
  groupName: string;
  messageHash: string;
  snippet: string;
  matchedAt: string;
}

// ─── Internal helpers ──────────────────────────────────────────────────────

function bytesToHex(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 1) s += (b[i] ?? 0).toString(16).padStart(2, '0');
  return s;
}

/**
 * Canonical pubkey context for cross-group mention indexing. Each group
 * uses its own hierarchical sub-key for membership unlinkability, but
 * the **mention inbox** rides a single dedicated sub-key so listMyMentions
 * can return a unified view without enumerating per-group keys.
 */
const SOCIAL_INBOX_CONTEXT = { environment: 'social' as const, purpose: 'inbox' };

function subscriptionId(ownPubkey: string, groupId: string): string {
  return `${ownPubkey}:${groupId}`;
}

function mentionId(messageHash: string, ownPubkey: string): string {
  return `${messageHash}:${ownPubkey}`;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ─── Manager ───────────────────────────────────────────────────────────────

export class SocialGroupManager {
  readonly #ctx: AppContext;
  readonly #threads: ThreadManager;
  /**
   * Phase 27.6: monotonic clock for structured-event cache writes.
   * Sequential `hidePost` → `restorePost` calls in the same ms otherwise
   * produce identical `sentAt`, and `#computeHiddenSet` then iterates rows
   * in primary-key (hash) order — making "latest action wins" non-
   * deterministic. We bump to at least `lastTs + 1ms` per write.
   */
  #lastEventMs = 0;

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
    this.#threads = new ThreadManager(ctx);
  }

  #nextMonotonicIso(): string {
    const now = Date.now();
    const next = Math.max(now, this.#lastEventMs + 1);
    this.#lastEventMs = next;
    return new Date(next).toISOString();
  }

  // ─── Group lifecycle ────────────────────────────────────────────────────

  async createGroup(opts: CreateGroupOptions): Promise<SocialGroupRecord> {
    if (typeof opts.name !== 'string' || opts.name.trim().length === 0) {
      throw new SocialValidationError('createGroup: name is required');
    }
    if (opts.name.length > 120) {
      throw new SocialValidationError('createGroup: name must be ≤120 chars');
    }
    const senderModel: SocialSenderModel = opts.senderModel ?? 'signed';
    if (opts.visibility === 'public-bulletin' && senderModel === 'signed') {
      // Allowed but warn-worthy — public posts under a real signature is
      // a privacy footgun. Caller is responsible; we don't block.
    }

    const ctx = this.#ctx;
    const groupId = uuid();
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));

    // Allocate the underlying MLS thread. context.purpose binds this thread
    // to this group, giving per-group identity unlinkability.
    const thread = await this.#threads.createThread({
      name: `social:${groupId}`,
      kind: 'chat',
      context: { environment: 'social', purpose: `group:${groupId}` },
    });

    const now = new Date().toISOString();
    const record: SocialGroupRecord = {
      id: groupId,
      name: opts.name,
      kind: opts.kind,
      mlsGroupId: thread.threadId,
      presentationStyle: opts.presentationStyle ?? opts.kind,
      visibility: opts.visibility,
      senderModel,
      ...(opts.defaultTtlSec !== undefined && { defaultTtlSec: opts.defaultTtlSec }),
      moderation: opts.moderation ?? 'admin-only',
      moderatorPubkeys: opts.moderatorPubkeys ?? [],
      federationLinkIds: opts.federationLinkIds ?? [],
      ...(opts.description !== undefined && { description: opts.description }),
      createdAt: now,
      createdBy: inboxKey,
    };

    const subscription: SocialSubscriptionRecord = {
      id: subscriptionId(inboxKey, groupId),
      ownPubkey: inboxKey,
      groupId,
      joinedAt: now,
      notify: 'all',
    };

    const db = ctx.storage.db;
    await db.transaction('rw', [db.social_groups, db.social_subscriptions], async () => {
      await db.social_groups.add(record);
      await db.social_subscriptions.add(subscription);
    });

    await this.audit({
      action: 'social.group-created',
      context: `social:group:${groupId}`,
      details: {
        groupId,
        kind: record.kind,
        visibility: record.visibility,
        senderModel: record.senderModel,
        mlsGroupId: record.mlsGroupId,
      },
    });

    return record;
  }

  async getGroup(groupId: string): Promise<SocialGroupRecord | undefined> {
    return this.#ctx.storage.db.social_groups.get(groupId);
  }

  /**
   * Phase 32.1: get-or-create a `kind: 'dm'` group between the local user
   * and a single peer (identified by their inbox-context pubkey).
   * Deterministic: the same pair always resolves to the same groupId so
   * both sides can converge on a single DM surface.
   *
   * The deterministic id is `dm:${sorted(self,peer).join(':')}` — peer
   * pair, alphabetically sorted, prefixed.
   */
  async getOrCreateDmGroup(peerInboxKey: string): Promise<SocialGroupRecord> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const myInbox = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    if (peerInboxKey === myInbox) {
      throw new SocialValidationError('getOrCreateDmGroup: cannot DM yourself');
    }
    const pair = [myInbox, peerInboxKey].sort();
    const dmId = `dm:${pair[0]}:${pair[1]}`;
    const existing = await db.social_groups.get(dmId);
    if (existing !== undefined) return existing;

    // Create the underlying MLS thread + group row.
    const thread = await this.#threads.createThread({
      name: `dm:${peerInboxKey.slice(0, 8)}`,
      kind: 'chat',
      context: { environment: 'social', purpose: `group:${dmId}` },
    });

    const now = new Date().toISOString();
    const record: SocialGroupRecord = {
      id: dmId,
      name: `DM · ${peerInboxKey.slice(0, 8)}…`,
      kind: 'dm',
      mlsGroupId: thread.threadId,
      presentationStyle: 'dm',
      visibility: 'private',
      senderModel: 'signed',
      moderation: 'admin-only',
      moderatorPubkeys: [],
      federationLinkIds: [],
      createdAt: now,
      createdBy: myInbox,
    };
    const subscription: SocialSubscriptionRecord = {
      id: subscriptionId(myInbox, dmId),
      ownPubkey: myInbox,
      groupId: dmId,
      joinedAt: now,
      notify: 'all',
    };

    await db.transaction('rw', [db.social_groups, db.social_subscriptions], async () => {
      await db.social_groups.add(record);
      await db.social_subscriptions.add(subscription);
    });

    await this.audit({
      action: 'social.dm-created',
      context: `social:group:${dmId}`,
      details: { dmId, peerPrefix: peerInboxKey.slice(0, 12) },
    });

    return record;
  }

  async listGroups(opts: { includeAll?: boolean; kind?: SocialGroupKind; includeArchived?: boolean } = {}): Promise<SocialGroupRecord[]> {
    const db = this.#ctx.storage.db;
    const includeArchived = opts.includeArchived === true;
    const all = await db.social_groups.orderBy('createdAt').reverse().toArray();
    const inboxKey = bytesToHex(await this.#ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));

    let filtered = all;
    if (opts.kind !== undefined) {
      filtered = filtered.filter((g) => g.kind === opts.kind);
    }
    if (!includeArchived) {
      filtered = filtered.filter((g) => g.archivedAt === undefined);
    }
    if (opts.includeAll !== true) {
      const myActiveGroupIds = new Set(
        (await db.social_subscriptions.where('ownPubkey').equals(inboxKey).toArray())
          .filter((s) => s.leftAt === undefined)
          .map((s) => s.groupId),
      );
      filtered = filtered.filter((g) => myActiveGroupIds.has(g.id));
    }
    return filtered;
  }

  async updateGroup(
    groupId: string,
    patch: Partial<Pick<SocialGroupRecord, 'name' | 'description' | 'presentationStyle' | 'defaultTtlSec' | 'moderatorPubkeys' | 'federationLinkIds'>>,
  ): Promise<SocialGroupRecord> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const existing = await db.social_groups.get(groupId);
    if (existing === undefined) {
      throw new SocialValidationError(`updateGroup: unknown groupId ${groupId}`);
    }
    if (existing.archivedAt !== undefined) {
      throw new SocialAuthError('group-archived');
    }
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const isMod = existing.createdBy === inboxKey || existing.moderatorPubkeys.includes(inboxKey);
    if (!isMod) throw new SocialAuthError('not-a-moderator');

    const next: SocialGroupRecord = {
      ...existing,
      ...(patch.name !== undefined && { name: patch.name }),
      ...(patch.description !== undefined && { description: patch.description }),
      ...(patch.presentationStyle !== undefined && { presentationStyle: patch.presentationStyle }),
      ...(patch.defaultTtlSec !== undefined && { defaultTtlSec: patch.defaultTtlSec }),
      ...(patch.moderatorPubkeys !== undefined && { moderatorPubkeys: patch.moderatorPubkeys }),
      ...(patch.federationLinkIds !== undefined && { federationLinkIds: patch.federationLinkIds }),
    };
    await db.social_groups.put(next);
    await this.audit({
      action: 'social.group-updated',
      context: `social:group:${groupId}`,
      details: { groupId, patchKeys: Object.keys(patch) },
    });
    return next;
  }

  async archiveGroup(groupId: string): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const existing = await db.social_groups.get(groupId);
    if (existing === undefined) {
      throw new SocialValidationError(`archiveGroup: unknown groupId ${groupId}`);
    }
    if (existing.archivedAt !== undefined) return;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const isMod = existing.createdBy === inboxKey || existing.moderatorPubkeys.includes(inboxKey);
    if (!isMod) throw new SocialAuthError('not-a-moderator');

    const now = new Date().toISOString();
    await db.social_groups.update(groupId, { archivedAt: now });
    await this.audit({
      action: 'social.group-archived',
      context: `social:group:${groupId}`,
      details: { groupId, archivedAt: now },
    });
  }

  // ─── Membership ─────────────────────────────────────────────────────────

  async joinGroup(
    groupId: string,
    _opts: { inviteCode?: string; membershipProof?: unknown } = {},
  ): Promise<SocialSubscriptionRecord> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(groupId);
    if (group === undefined) {
      throw new SocialValidationError(`joinGroup: unknown groupId ${groupId}`);
    }
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');

    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const id = subscriptionId(inboxKey, groupId);
    const existing = await db.social_subscriptions.get(id);
    const now = new Date().toISOString();

    let next: SocialSubscriptionRecord;
    if (existing !== undefined) {
      // Re-join: clear leftAt, reset notify if previously off.
      next = {
        ...existing,
        joinedAt: now,
        notify: existing.notify === 'off' ? 'mentions' : existing.notify,
      };
      delete next.leftAt;
      await db.social_subscriptions.put(next);
    } else {
      next = {
        id,
        ownPubkey: inboxKey,
        groupId,
        joinedAt: now,
        notify: 'all',
      };
      await db.social_subscriptions.add(next);
    }

    await this.audit({
      action: 'social.group-joined',
      context: `social:group:${groupId}`,
      details: { groupId, rejoin: existing !== undefined },
    });
    return next;
  }

  async leaveGroup(groupId: string): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const id = subscriptionId(inboxKey, groupId);
    const existing = await db.social_subscriptions.get(id);
    if (existing === undefined || existing.leftAt !== undefined) return;

    const now = new Date().toISOString();
    await db.social_subscriptions.update(id, { leftAt: now });
    await this.audit({
      action: 'social.group-left',
      context: `social:group:${groupId}`,
      details: { groupId, leftAt: now },
    });
  }

  /**
   * Return true if the current user is a moderator (creator or in
   * `moderatorPubkeys`) of the group. Used by surfaces to decide whether
   * to show the composer in write-gated groups (broadcast).
   */
  async isModerator(groupId: string): Promise<boolean> {
    const ctx = this.#ctx;
    const group = await ctx.storage.db.social_groups.get(groupId);
    if (group === undefined) return false;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    return group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
  }

  async listMembers(groupId: string): Promise<string[]> {
    const rows = await this.#ctx.storage.db.social_subscriptions
      .where('groupId')
      .equals(groupId)
      .toArray();
    return rows.filter((r) => r.leftAt === undefined).map((r) => r.ownPubkey);
  }

  async setNotifyPreference(groupId: string, pref: SocialSubscriptionRecord['notify']): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const id = subscriptionId(inboxKey, groupId);
    const existing = await db.social_subscriptions.get(id);
    if (existing === undefined || existing.leftAt !== undefined) {
      throw new SocialAuthError('not-a-member');
    }
    await db.social_subscriptions.update(id, { notify: pref });
    await this.audit({
      action: 'social.notify-preference-set',
      context: `social:group:${groupId}`,
      details: { groupId, notify: pref },
    });
  }

  async setPresentationOverride(groupId: string, style?: SocialPresentationStyle): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const id = subscriptionId(inboxKey, groupId);
    const existing = await db.social_subscriptions.get(id);
    if (existing === undefined || existing.leftAt !== undefined) {
      throw new SocialAuthError('not-a-member');
    }
    if (style === undefined) {
      // Clear override.
      const cleared: SocialSubscriptionRecord = { ...existing };
      delete cleared.presentationOverride;
      await db.social_subscriptions.put(cleared);
    } else {
      await db.social_subscriptions.update(id, { presentationOverride: style });
    }
    await this.audit({
      action: 'social.presentation-override-set',
      context: `social:group:${groupId}`,
      details: { groupId, presentationOverride: style ?? null },
    });
  }

  // ─── Compose / publish ──────────────────────────────────────────────────

  async publish(opts: PublishOptions): Promise<PublishResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(opts.groupId);
    if (group === undefined) {
      throw new SocialValidationError(`publish: unknown groupId ${opts.groupId}`);
    }
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');
    // Phase 29.2: mirrored groups are read-only.
    if (group.mirrored === true) {
      throw new SocialValidationError(`publish: group ${opts.groupId} is a federated mirror (read-only)`);
    }

    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const sub = await db.social_subscriptions.get(subscriptionId(inboxKey, opts.groupId));
    if (sub === undefined || sub.leftAt !== undefined) {
      throw new SocialAuthError('not-a-member');
    }

    // Broadcast groups are write-gated: only moderators (creator + listed
    // moderatorPubkeys) can publish top-level posts. Replies are allowed
    // for everyone so members can still engage. This matches the
    // Telegram-channel one-way pattern from the Phase 27 design.
    if (group.kind === 'broadcast' && opts.parentHash === undefined) {
      const isMod = group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
      if (!isMod) throw new SocialAuthError('not-a-moderator');
    }

    // Phase 55: in community-vote-prepublish groups, top-level posts must
    // go through ModerationManager.submitForReview. Replies skip review
    // (they're attached to an already-approved root). ModerationManager
    // calls publish() with `moderation` temporarily flipped to
    // 'admin-only' so this check is bypassed during the finalize step.
    if (group.moderation === 'community-vote-prepublish' && opts.parentHash === undefined) {
      throw new SocialValidationError(
        `publish: group ${opts.groupId} uses community-vote-prepublish — submit via ModerationManager.submitForReview`,
      );
    }

    if ((opts.text === undefined || opts.text.length === 0)
        && (opts.mediaManifestIds === undefined || opts.mediaManifestIds.length === 0)
        && (opts.pollOptions === undefined || opts.pollOptions.length === 0)
        && opts.linkCardUrl === undefined
        && opts.voiceNoteBlob === undefined) {
      throw new SocialValidationError('publish: post must have text, media, link, poll, or voice note');
    }

    // Phase 46.1 (v9): voice-note Vault integration. When a blob is
    // provided, store it via Vault BEFORE we sign the social post, so the
    // resulting manifest id can be appended to `mediaManifestIds`. Vault
    // advances the MLS state once; we persist that state immediately so
    // the subsequent `ThreadManager.sendMessage` loads from the advanced
    // state correctly.
    const mediaIdsOut = [...(opts.mediaManifestIds ?? [])];
    if (opts.voiceNoteBlob !== undefined) {
      try {
        const { Vault } = await import('../vault/Vault');
        const vault = new Vault({ groupEncryption: ctx.groupEnc, storage: ctx.storage });
        const { ThreadManager } = await import('../threads/ThreadManager');
        const tm = new ThreadManager(ctx);
        const groupState = await tm._loadGroupState(group.mlsGroupId);
        const bytes = new Uint8Array(await opts.voiceNoteBlob.arrayBuffer());
        const filename = opts.voiceNoteFilename ?? `voice-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
        const contentType = opts.voiceNoteMimeType ?? opts.voiceNoteBlob.type ?? 'audio/webm';
        const { manifest, groupStateAfterStore } = await vault.store({
          groupState,
          filename,
          contentType,
          bytes,
        });
        // Persist advanced MLS state + manifest cache row.
        const now = new Date().toISOString();
        const newMlsRecord: import('../storage/schema').MlsStateRecord = {
          threadId: group.mlsGroupId,
          context: `social:group:${opts.groupId}`,
          epoch: groupStateAfterStore.epoch,
          memberCount: groupStateAfterStore.memberCount,
          cipherSuite: groupStateAfterStore.impl === 'plaintext' ? 'plaintext' : 'mls',
          ciphertext: await ctx.storage.encryptForSession(groupStateAfterStore.opaque),
          updatedAt: now,
        };
        await db.mls_state.put(newMlsRecord);
        const cacheRow: import('../storage/schema').ManifestCacheRecord = {
          manifestId: manifest.manifestId,
          groupId: group.mlsGroupId,
          context: `social:group:${opts.groupId}`,
          filename,
          contentType,
          totalSize: manifest.totalSize,
          chunkCount: manifest.chunks.length,
          senderPubkey: inboxKey,
          createdAt: now,
          epochAtCreation: groupStateAfterStore.epoch,
          chunksLocal: true,
          tombstoned: false,
        };
        await db.manifests_cache.put(cacheRow);
        // Prefix the manifest id with `audio:` so feed renderers can detect
        // voice notes without an extra DB lookup (and so Phase 41's
        // existing "🎙 N voice notes" badge keeps working).
        mediaIdsOut.push(`audio:${manifest.manifestId}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`SocialGroupManager.publish: voice-note Vault store failed: ${errMsg(err)}`);
        // Continue without the voice note rather than failing the whole publish.
      }
    }

    const payload: SocialPostPayload = {
      kind: 'social-post',
      groupId: opts.groupId,
      ...(opts.text !== undefined && { text: opts.text }),
      ...(mediaIdsOut.length > 0 && { mediaManifestIds: mediaIdsOut }),
      ...(opts.linkCardUrl !== undefined && { linkCardUrl: opts.linkCardUrl }),
      ...(opts.parentHash !== undefined && { parentHash: opts.parentHash }),
      ...(opts.tags !== undefined && { tags: opts.tags.map((t) => t.toLowerCase().replace(/^#/, '')) }),
      ...(opts.mentionPubkeys !== undefined && { mentionPubkeys: opts.mentionPubkeys }),
      ...(opts.pollOptions !== undefined && { pollOptions: opts.pollOptions }),
      ...(opts.ttlSec !== undefined
        ? { ttlSec: opts.ttlSec }
        : group.defaultTtlSec !== undefined && { ttlSec: group.defaultTtlSec }),
      ...(opts.story !== undefined && { story: opts.story }),
      ...(group.senderModel === 'sealed-sender' && { sealedSender: true }),
    };

    // Phase 28.2: attach a Semaphore proof of neighborhood membership when
    // sealed-sender is enabled. The proof binds an external verifier's
    // confidence that the author was *some* member of the neighborhood at
    // publish time, without revealing which. Within-group cryptographic
    // unlinkability requires MLS-layer signer-key rotation (deferred).
    if (group.senderModel === 'sealed-sender') {
      try {
        const { MeshManager } = await import('../mesh/MeshManager');
        const { MembershipManager } = await import('../membership/MembershipManager');
        const neighborhoodId = await new MeshManager(ctx).neighborhoodId();
        const mm = new MembershipManager(ctx);
        // Bind the proof to the (groupId, payload hash) so the proof can't
        // be replayed onto a different envelope.
        const { blake3 } = await import('@noble/hashes/blake3');
        const messageBinding = bytesToHex(blake3(new TextEncoder().encode(JSON.stringify(payload)))).slice(0, 32);
        const proof = await mm.generateProofForIncident({
          neighborhoodId,
          externalNullifier: `social-sealed:${opts.groupId}`,
          message: messageBinding,
        });
        payload.sealedSenderProof = { neighborhoodId, proof };
      } catch (err) {
        // If membership isn't bootstrapped, fall back to presentation-only sealed sender.
        // eslint-disable-next-line no-console
        console.warn(`SocialGroupManager.publish: sealed-sender proof skipped: ${errMsg(err)}`);
      }
    }

    // Encrypt + sign + append via ThreadManager. The payload travels as
    // the encrypted body of an MLS message on the underlying thread.
    const payloadJson = JSON.stringify(payload);
    const sendResult = await this.#threads.sendMessage({
      threadId: group.mlsGroupId,
      text: payloadJson,
    });

    const publishedAt = new Date().toISOString();

    // Loopback: write the author's own outbound post into message_cache so
    // their feed shows it immediately. The inbox loopback-skip prevents
    // this from happening via the inbox path, so we do it here explicitly.
    // Threads' sendMessage intentionally doesn't do this (point-to-point
    // chat UX shows sent messages from a separate "sent" rail); social UX
    // needs the post in the same feed everyone else sees.
    const groupContextKey = `social:group:${opts.groupId}`;
    const ownGroupPubkey = bytesToHex(
      await ctx.identity.pubkey({ environment: 'social', purpose: `group:${opts.groupId}` }),
    );
    const cacheRecord: MessageCacheRecord = {
      hash: sendResult.envelopeHash,
      context: groupContextKey,
      threadId: group.mlsGroupId,
      senderPubkey: ownGroupPubkey,
      lamport: 0,
      sentAt: publishedAt,
      receivedAt: publishedAt,
      kind: 'text' as MessageKind,
      ciphertext: await ctx.storage.encryptForSession(new TextEncoder().encode(payloadJson)),
    };
    try {
      await db.message_cache.put(cacheRecord);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`SocialGroupManager.publish: cache write failed: ${errMsg(err)}`);
    }

    // Self-mention indexing. Recipient-side mention indexing for *other*
    // pubkeys is handled by `ingestIncomingMentions` (pull-based; called
    // explicitly or on tick).
    if (opts.mentionPubkeys !== undefined && opts.mentionPubkeys.includes(inboxKey)) {
      const mention: SocialMentionRecord = {
        id: mentionId(sendResult.envelopeHash, inboxKey),
        ownPubkey: inboxKey,
        messageHash: sendResult.envelopeHash,
        groupId: opts.groupId,
        receivedAt: publishedAt,
      };
      try {
        await db.social_mentions.put(mention);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`SocialGroupManager.publish: mention index failed: ${errMsg(err)}`);
      }
    }

    // Push wakeup (Phase 27.5; refactored 27.8): delegate to the unified
    // WakeupNotifier. Trust trade-off (plaintext routing metadata to the
    // relay) is documented in `docs/alds-push-relay.md`.
    if (opts.mentionPubkeys !== undefined && opts.mentionPubkeys.length > 0) {
      const { getWakeupNotifier } = await import('../credentials/WakeupNotifier');
      const notifier = getWakeupNotifier(ctx);
      const targets = opts.mentionPubkeys.filter((p) => p !== inboxKey);
      for (const targetPubkey of targets) {
        void notifier.wake({
          targetPubkey,
          kind: 'alds-mention-wakeup',
          details: { groupId: opts.groupId, messageHash: sendResult.envelopeHash },
        }).catch(() => { /* swallow */ });
      }
    }

    // Phase 27.7: federation auto-forward. For each active link in
    // group.federationLinkIds, fire-and-forget a forwardSocialPost. The
    // forwarder applies redaction; receivers re-apply on ingest.
    if (group.federationLinkIds.length > 0) {
      const links = await Promise.all(
        group.federationLinkIds.map((id) => db.federation_links.get(id)),
      );
      const activeLinks = links.filter(
        (l): l is import('../storage/schema').FederationLinkRecord =>
          l !== undefined && l.status === 'active',
      );
      if (activeLinks.length > 0) {
        // Lazy import to avoid hard cycle with FederationManager → social.
        const { FederationManager } = await import('../federation/FederationManager');
        const fm = new FederationManager(ctx);
        for (const link of activeLinks) {
          void fm.forwardSocialPost({
            groupId: opts.groupId,
            messageHash: sendResult.envelopeHash,
            remoteNeighborhoodId: link.remoteNeighborhoodId,
          }).catch(() => { /* swallow — forwarding is best-effort */ });
        }
      }
    }

    await this.audit({
      action: 'social.post-published',
      context: `social:group:${opts.groupId}`,
      details: {
        groupId: opts.groupId,
        messageHash: sendResult.envelopeHash,
        hasText: opts.text !== undefined,
        mediaCount: opts.mediaManifestIds?.length ?? 0,
        mentionCount: opts.mentionPubkeys?.length ?? 0,
        ttlSec: payload.ttlSec ?? null,
        story: opts.story === true,
      },
    });

    return {
      messageHash: sendResult.envelopeHash,
      envelopeHash: sendResult.envelopeHash,
      publishedAt,
    };
  }

  /**
   * Publish an ephemeral story. Convenience wrapper over `publish` that
   * defaults TTL to 24h and sets `story: true`. Caller may override TTL via
   * `ttlSec`. Stories appear in the `social-stories` surface only — they
   * don't show up in the default feed list (renderer-side filter).
   */
  async publishStory(opts: Omit<PublishOptions, 'story' | 'parentHash'>): Promise<PublishResult> {
    return this.publish({
      ...opts,
      ttlSec: opts.ttlSec ?? 24 * 60 * 60,
      story: true,
    });
  }

  /**
   * Return the user's current (non-expired) stories for a group, oldest
   * first (rendering order is left→right like Instagram). Filters posts to
   * `story: true` and `sentAt + ttlSec > now`.
   */
  async listStories(opts: { groupId: string; now?: Date }): Promise<ListedMessage[]> {
    const group = await this.#ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) {
      throw new SocialValidationError(`listStories: unknown groupId ${opts.groupId}`);
    }
    const nowMs = (opts.now ?? new Date()).getTime();
    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 400 });
    const stories: ListedMessage[] = [];
    for (const r of rows) {
      const payload = this.#decodePostPayload(r.text, opts.groupId);
      if (payload === null) continue;
      if (payload.story !== true) continue;
      if (payload.ttlSec === undefined) continue;
      const expiresMs = new Date(r.sentAt).getTime() + payload.ttlSec * 1000;
      if (expiresMs <= nowMs) continue;
      stories.push({
        messageHash: r.hash,
        senderPubkey: r.senderPubkey,
        sentAt: r.sentAt,
        payload,
      });
    }
    stories.sort((a, b) => {
      if (a.sentAt < b.sentAt) return -1;
      if (a.sentAt > b.sentAt) return 1;
      return a.messageHash < b.messageHash ? -1 : a.messageHash > b.messageHash ? 1 : 0;
    });
    return stories;
  }

  async editPost(_groupId: string, _targetHash: string, _newText: string): Promise<void> {
    throw new NotImplementedInPhase27('editPost');
  }

  async react(_groupId: string, _targetHash: string, _emoji: string, _add: boolean = true): Promise<void> {
    throw new NotImplementedInPhase27('react');
  }

  /**
   * Cast (or change) a vote on a poll. Publishes a signed
   * `social-poll-vote` envelope on the underlying thread. Latest vote per
   * voter wins — replaying with a different `choice` supersedes prior votes
   * by the same author (handled at tally time).
   *
   * Validates that the target post exists in this group, has `pollOptions`,
   * and that `choice` is within range. Returns the vote envelope hash for
   * audit/cite.
   */
  async votePoll(opts: { groupId: string; pollHash: string; choice: number }): Promise<{ envelopeHash: string }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`votePoll: unknown groupId ${opts.groupId}`);
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');

    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const sub = await db.social_subscriptions.get(subscriptionId(inboxKey, opts.groupId));
    if (sub === undefined || sub.leftAt !== undefined) throw new SocialAuthError('not-a-member');

    // Resolve the target poll post + validate choice range.
    const all = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 400 });
    const target = all.find((r) => r.hash === opts.pollHash);
    if (target === undefined) {
      throw new SocialValidationError(`votePoll: pollHash not found in group ${opts.groupId}`);
    }
    const targetPayload = this.#decodePostPayload(target.text, opts.groupId);
    if (targetPayload === null || targetPayload.pollOptions === undefined) {
      throw new SocialValidationError('votePoll: target message is not a poll');
    }
    if (!Number.isInteger(opts.choice) || opts.choice < 0 || opts.choice >= targetPayload.pollOptions.length) {
      throw new SocialValidationError(`votePoll: choice ${opts.choice} out of range [0, ${targetPayload.pollOptions.length})`);
    }

    const payload: SocialPollVotePayload = {
      kind: 'social-poll-vote',
      groupId: opts.groupId,
      pollHash: opts.pollHash,
      choice: opts.choice,
    };
    const payloadJson = JSON.stringify(payload);
    const sendResult = await this.#threads.sendMessage({ threadId: group.mlsGroupId, text: payloadJson });

    const sentAt = new Date().toISOString();
    const ownGroupPubkey = bytesToHex(
      await ctx.identity.pubkey({ environment: 'social', purpose: `group:${opts.groupId}` }),
    );
    const cacheRecord: MessageCacheRecord = {
      hash: sendResult.envelopeHash,
      context: `social:group:${opts.groupId}`,
      threadId: group.mlsGroupId,
      senderPubkey: ownGroupPubkey,
      lamport: 0,
      sentAt,
      receivedAt: sentAt,
      kind: 'reaction' as MessageKind,  // closest existing MessageKind to "structured non-text" event
      ciphertext: await ctx.storage.encryptForSession(new TextEncoder().encode(payloadJson)),
    };
    try { await db.message_cache.put(cacheRecord); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`SocialGroupManager.votePoll: cache write failed: ${errMsg(err)}`);
    }

    await this.audit({
      action: 'social.poll-voted',
      context: `social:group:${opts.groupId}`,
      details: { groupId: opts.groupId, pollHash: opts.pollHash, choice: opts.choice },
    });

    return { envelopeHash: sendResult.envelopeHash };
  }

  /**
   * Return the current vote tally for a poll. Counts the LATEST vote per
   * voter (by `sentAt`), so vote changes work cleanly. Returns one count
   * per `pollOptions[]` index plus a total.
   */
  async tallyPoll(opts: { groupId: string; pollHash: string }): Promise<{
    counts: number[];
    total: number;
    options: string[];
  }> {
    const group = await this.#ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`tallyPoll: unknown groupId ${opts.groupId}`);

    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 1000 });

    // Find target poll + its options.
    const target = rows.find((r) => r.hash === opts.pollHash);
    if (target === undefined) {
      throw new SocialValidationError(`tallyPoll: pollHash not found in group ${opts.groupId}`);
    }
    const targetPayload = this.#decodePostPayload(target.text, opts.groupId);
    if (targetPayload === null || targetPayload.pollOptions === undefined) {
      throw new SocialValidationError('tallyPoll: target message is not a poll');
    }

    // Collect latest vote per voter.
    const latestByVoter = new Map<string, { choice: number; sentAt: string }>();
    for (const r of rows) {
      let parsed: SocialPollVotePayload | null = null;
      try {
        const obj = JSON.parse(r.text);
        if (obj && typeof obj === 'object' && obj.kind === 'social-poll-vote'
            && obj.groupId === opts.groupId && obj.pollHash === opts.pollHash
            && typeof obj.choice === 'number') {
          parsed = obj as SocialPollVotePayload;
        }
      } catch { /* skip */ }
      if (parsed === null) continue;
      const prev = latestByVoter.get(r.senderPubkey);
      if (prev === undefined || prev.sentAt < r.sentAt) {
        latestByVoter.set(r.senderPubkey, { choice: parsed.choice, sentAt: r.sentAt });
      }
    }

    const counts = new Array(targetPayload.pollOptions.length).fill(0) as number[];
    for (const v of latestByVoter.values()) {
      if (v.choice >= 0 && v.choice < counts.length) {
        counts[v.choice] = (counts[v.choice] ?? 0) + 1;
      }
    }
    const total = counts.reduce((a, b) => a + b, 0);
    return { counts, total, options: targetPayload.pollOptions };
  }

  /**
   * Phase 27.6 moderation — hide a post. Authority gate:
   *   - admin-only groups: creator OR `moderatorPubkeys` member
   *   - mod-team groups: any `moderatorPubkeys` member (creator implicit)
   *   - community-vote groups: any member CAN publish a `social-hide` but
   *     it carries no immediate effect; the canonical hide comes from
   *     quorum on `voteHide` (see `voteHide` / `tallyHideVotes`).
   *
   * Publishes a `social-hide` envelope on the underlying thread. The
   * envelope persists in the encrypted log — the original post is NOT
   * deleted; clients render a "hidden by moderator" placeholder.
   */
  async hidePost(opts: { groupId: string; targetHash: string; reason?: string }): Promise<{ envelopeHash: string }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`hidePost: unknown groupId ${opts.groupId}`);
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');

    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const sub = await db.social_subscriptions.get(subscriptionId(inboxKey, opts.groupId));
    if (sub === undefined || sub.leftAt !== undefined) throw new SocialAuthError('not-a-member');

    const isMod = group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
    if (!isMod && group.moderation !== 'community-vote') {
      throw new SocialAuthError('not-a-moderator');
    }
    // Note: in community-vote mode any member may emit a `social-hide` for
    // transparency, but `isHidden` does not honor it unless quorum on
    // `voteHide` has been reached. The audit log makes the authority
    // visible to all clients.

    const payload: SocialHidePayload = {
      kind: 'social-hide',
      groupId: opts.groupId,
      targetHash: opts.targetHash,
      senderInboxKey: inboxKey,
      ...(opts.reason !== undefined ? { reason: opts.reason } : {}),
    };
    const payloadJson = JSON.stringify(payload);
    const sendResult = await this.#threads.sendMessage({ threadId: group.mlsGroupId, text: payloadJson });
    await this.#cacheStructuredEvent(opts.groupId, group.mlsGroupId, sendResult.envelopeHash, payloadJson);

    await this.audit({
      action: 'social.post-hidden',
      context: `social:group:${opts.groupId}`,
      details: { groupId: opts.groupId, targetHash: opts.targetHash, reason: opts.reason ?? null, byMod: isMod },
    });
    return { envelopeHash: sendResult.envelopeHash };
  }

  /**
   * Phase 27.6 moderation — undo a hide. Same authority gate as
   * `hidePost`. Publishes a `social-restore` envelope; clients revert
   * the hidden-by-moderator state.
   */
  async restorePost(opts: { groupId: string; targetHash: string }): Promise<{ envelopeHash: string }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`restorePost: unknown groupId ${opts.groupId}`);
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');

    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const sub = await db.social_subscriptions.get(subscriptionId(inboxKey, opts.groupId));
    if (sub === undefined || sub.leftAt !== undefined) throw new SocialAuthError('not-a-member');

    const isMod = group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
    if (!isMod) throw new SocialAuthError('not-a-moderator');

    const payload: SocialRestorePayload = {
      kind: 'social-restore',
      groupId: opts.groupId,
      targetHash: opts.targetHash,
      senderInboxKey: inboxKey,
    };
    const payloadJson = JSON.stringify(payload);
    const sendResult = await this.#threads.sendMessage({ threadId: group.mlsGroupId, text: payloadJson });
    await this.#cacheStructuredEvent(opts.groupId, group.mlsGroupId, sendResult.envelopeHash, payloadJson);

    await this.audit({
      action: 'social.post-restored',
      context: `social:group:${opts.groupId}`,
      details: { groupId: opts.groupId, targetHash: opts.targetHash },
    });
    return { envelopeHash: sendResult.envelopeHash };
  }

  /**
   * Cast a community-vote hide. Allowed only when group.moderation ===
   * 'community-vote'. Quorum tally is read by `isHidden` / `tallyHideVotes`.
   */
  async voteHide(opts: { groupId: string; targetHash: string; reason?: string }): Promise<{ envelopeHash: string }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`voteHide: unknown groupId ${opts.groupId}`);
    if (group.moderation !== 'community-vote') {
      throw new SocialValidationError(`voteHide: group moderation policy is '${group.moderation}', not 'community-vote'`);
    }
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const sub = await db.social_subscriptions.get(subscriptionId(inboxKey, opts.groupId));
    if (sub === undefined || sub.leftAt !== undefined) throw new SocialAuthError('not-a-member');

    const payload: SocialHideVotePayload = {
      kind: 'social-hide-vote',
      groupId: opts.groupId,
      targetHash: opts.targetHash,
      senderInboxKey: inboxKey,
      ...(opts.reason !== undefined ? { reason: opts.reason } : {}),
    };
    const payloadJson = JSON.stringify(payload);
    const sendResult = await this.#threads.sendMessage({ threadId: group.mlsGroupId, text: payloadJson });
    await this.#cacheStructuredEvent(opts.groupId, group.mlsGroupId, sendResult.envelopeHash, payloadJson);

    await this.audit({
      action: 'social.hide-voted',
      context: `social:group:${opts.groupId}`,
      details: { groupId: opts.groupId, targetHash: opts.targetHash, reason: opts.reason ?? null },
    });
    return { envelopeHash: sendResult.envelopeHash };
  }

  /**
   * Count unique voters for a community-vote hide on a specific target.
   * Returns `{voters, quorum, met}` where `met` reflects whether
   * `voters.length >= quorum`. Default quorum: `ceil(members / 2)`.
   */
  async tallyHideVotes(opts: { groupId: string; targetHash: string; quorum?: number }): Promise<{
    voters: string[];
    quorum: number;
    met: boolean;
  }> {
    const ctx = this.#ctx;
    const group = await ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) throw new SocialValidationError(`tallyHideVotes: unknown groupId ${opts.groupId}`);

    const members = await this.listMembers(opts.groupId);
    const defaultQuorum = Math.max(1, Math.ceil(members.length / 2));
    const quorum = opts.quorum ?? defaultQuorum;

    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 1000 });
    const voters = new Set<string>();
    for (const r of rows) {
      let parsed: unknown;
      try { parsed = JSON.parse(r.text); } catch { continue; }
      if (parsed && typeof parsed === 'object' && (parsed as { kind?: string }).kind === 'social-hide-vote') {
        const p = parsed as SocialHideVotePayload;
        if (p.groupId === opts.groupId && p.targetHash === opts.targetHash) {
          voters.add(r.senderPubkey);
        }
      }
    }
    const arr = [...voters];
    return { voters: arr, quorum, met: arr.length >= quorum };
  }

  /**
   * Return whether a target post is currently hidden, honoring the group's
   * moderation policy:
   *   - admin-only / mod-team: a `social-hide` from creator/mod hides;
   *     a later `social-restore` from creator/mod un-hides.
   *   - community-vote: hidden iff `tallyHideVotes` meets quorum AND no
   *     subsequent `social-restore` from a mod overrides.
   */
  async isHidden(opts: { groupId: string; targetHash: string }): Promise<boolean> {
    const ctx = this.#ctx;
    const group = await ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) return false;

    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 1000 });
    // Same deterministic ordering as #computeHiddenSet.
    const sorted = rows.slice().sort((a, b) => {
      if (a.sentAt < b.sentAt) return -1;
      if (a.sentAt > b.sentAt) return 1;
      return a.hash < b.hash ? -1 : a.hash > b.hash ? 1 : 0;
    });
    let lastModAction: { kind: 'hide' | 'restore'; sentAt: string } | null = null;
    for (const r of sorted) {
      let parsed: { kind?: string; groupId?: string; targetHash?: string; senderInboxKey?: string } | null = null;
      try { parsed = JSON.parse(r.text); } catch { continue; }
      if (parsed === null) continue;
      if (parsed.groupId !== opts.groupId || parsed.targetHash !== opts.targetHash) continue;
      // Phase 28.3: prefer inbox-key claim from payload (cross-peer mod check).
      const author = parsed.senderInboxKey ?? r.senderPubkey;
      const isMod = group.createdBy === author || group.moderatorPubkeys.includes(author);
      if (parsed.kind === 'social-hide') {
        // Phase 28.3: strict mod check. Any peer can include a
        // `senderInboxKey` claim; only when that claim resolves to a real
        // mod do we treat the envelope as authoritative.
        if (!isMod) continue;
        if (lastModAction === null || lastModAction.sentAt <= r.sentAt) {
          lastModAction = { kind: 'hide', sentAt: r.sentAt };
        }
      } else if (parsed.kind === 'social-restore' && isMod) {
        if (lastModAction === null || lastModAction.sentAt <= r.sentAt) {
          lastModAction = { kind: 'restore', sentAt: r.sentAt };
        }
      }
    }

    if (lastModAction !== null) {
      return lastModAction.kind === 'hide';
    }

    // No authoritative mod action; for community-vote check quorum.
    if (group.moderation === 'community-vote') {
      const tally = await this.tallyHideVotes({ groupId: opts.groupId, targetHash: opts.targetHash });
      return tally.met;
    }
    return false;
  }

  /**
   * List currently-hidden message hashes in a group. Useful for moderator
   * dashboards.
   */
  async listHiddenPosts(groupId: string): Promise<string[]> {
    const ctx = this.#ctx;
    const group = await ctx.storage.db.social_groups.get(groupId);
    if (group === undefined) return [];
    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: 1000 });

    // Collect candidate target hashes from hide-or-vote envelopes.
    const candidates = new Set<string>();
    for (const r of rows) {
      try {
        const parsed = JSON.parse(r.text);
        if (parsed && typeof parsed === 'object' && parsed.groupId === groupId) {
          if (parsed.kind === 'social-hide' || parsed.kind === 'social-hide-vote') {
            candidates.add(parsed.targetHash);
          }
        }
      } catch { /* skip */ }
    }
    const hidden: string[] = [];
    for (const t of candidates) {
      if (await this.isHidden({ groupId, targetHash: t })) hidden.push(t);
    }
    return hidden;
  }

  /**
   * Internal: write a structured-event envelope into local message_cache.
   * Mirrors the loopback path in `publish` so we don't depend on inbox
   * hydration for our own emitted hide/restore/vote envelopes.
   */
  async #cacheStructuredEvent(
    groupId: string,
    mlsGroupId: string,
    envelopeHash: string,
    payloadJson: string,
  ): Promise<void> {
    const ctx = this.#ctx;
    const now = this.#nextMonotonicIso();
    // Cache rows use the **inbox** sub-key as `senderPubkey` so that local
    // moderation checks (`#computeHiddenSet`, `isHidden`) — which compare
    // against `group.createdBy` + `moderatorPubkeys` (both inbox-context
    // pubkeys) — recognize our own hide/restore events. The wire envelope
    // is still signed with the group-context sub-key for unlinkability,
    // and peer-authored mod actions arriving through the inbox loop will
    // need a separate mapping step (deferred Phase 27.x).
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const cacheRecord: MessageCacheRecord = {
      hash: envelopeHash,
      context: `social:group:${groupId}`,
      threadId: mlsGroupId,
      senderPubkey: inboxKey,
      lamport: 0,
      sentAt: now,
      receivedAt: now,
      kind: 'reaction' as MessageKind,
      ciphertext: await ctx.storage.encryptForSession(new TextEncoder().encode(payloadJson)),
    };
    try { await ctx.storage.db.message_cache.put(cacheRecord); }
    catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`SocialGroupManager.#cacheStructuredEvent: ${errMsg(err)}`);
    }
  }

  // ─── Read ───────────────────────────────────────────────────────────────

  async listMessages(opts: ListMessagesOptions): Promise<ListedMessage[]> {
    const group = await this.#ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) {
      throw new SocialValidationError(`listMessages: unknown groupId ${opts.groupId}`);
    }
    const limit = opts.limit ?? 50;
    const order = opts.order ?? 'desc';
    const kindFilter = opts.kindFilter ?? 'posts-only';

    // Pull more rows than we'll return — replies share the underlying
    // thread so we need them visible to compute reply counts even when we
    // filter them out of the top-level result.
    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: limit * 8 });

    const allDecoded: ListedMessage[] = [];
    for (const r of rows) {
      const payload = this.#decodePostPayload(r.text, opts.groupId);
      if (payload === null) continue;
      allDecoded.push({
        messageHash: r.hash,
        senderPubkey: r.senderPubkey,
        sentAt: r.sentAt,
        payload,
      });
    }

    // Index reply parents → count for fast lookup.
    const replyCountByParent = new Map<string, number>();
    if (opts.includeReplyCount === true) {
      for (const m of allDecoded) {
        const p = m.payload.parentHash;
        if (p !== undefined) replyCountByParent.set(p, (replyCountByParent.get(p) ?? 0) + 1);
      }
    }

    // Phase 27.6: derive hidden set once over the same row pool.
    const hiddenSet = this.#computeHiddenSet(group, rows);

    let decoded = allDecoded;
    if (kindFilter === 'posts-only') {
      decoded = decoded.filter((m) => m.payload.parentHash === undefined);
    }
    if (opts.includeStories !== true) {
      decoded = decoded.filter((m) => m.payload.story !== true);
    }

    decoded = decoded.map((m) => {
      const out: ListedMessage = { ...m, hidden: hiddenSet.has(m.messageHash) };
      if (opts.includeReplyCount === true) {
        out.replyCount = replyCountByParent.get(m.messageHash) ?? 0;
      }
      return out;
    });

    if (opts.includeHidden !== true) {
      decoded = decoded.filter((m) => m.hidden !== true);
    }

    decoded.sort((a, b) => {
      if (a.sentAt < b.sentAt) return -1;
      if (a.sentAt > b.sentAt) return 1;
      return a.messageHash < b.messageHash ? -1 : a.messageHash > b.messageHash ? 1 : 0;
    });
    let result = order === 'desc' ? decoded.slice().reverse() : decoded;
    if (opts.before !== undefined) {
      const idx = result.findIndex((m) => m.messageHash === opts.before);
      if (idx >= 0) result = result.slice(idx + 1);
    }
    return result.slice(0, limit);
  }

  /**
   * Return replies (posts whose `parentHash === parentHash`) for a given
   * top-level post. Default order is ascending (oldest first — natural
   * threaded conversation order).
   */
  async listReplies(opts: ListRepliesOptions): Promise<ListedMessage[]> {
    const group = await this.#ctx.storage.db.social_groups.get(opts.groupId);
    if (group === undefined) {
      throw new SocialValidationError(`listReplies: unknown groupId ${opts.groupId}`);
    }
    const limit = opts.limit ?? 100;
    const order = opts.order ?? 'asc';

    const rows = await this.#threads.getMessages({ threadId: group.mlsGroupId, limit: limit * 8 });
    const hiddenSet = this.#computeHiddenSet(group, rows);
    let replies: ListedMessage[] = [];
    for (const r of rows) {
      const payload = this.#decodePostPayload(r.text, opts.groupId);
      if (payload === null) continue;
      if (payload.parentHash !== opts.parentHash) continue;
      replies.push({
        messageHash: r.hash,
        senderPubkey: r.senderPubkey,
        sentAt: r.sentAt,
        payload,
        hidden: hiddenSet.has(r.hash),
      });
    }
    if (opts.includeHidden !== true) {
      replies = replies.filter((m) => m.hidden !== true);
    }
    replies.sort((a, b) => {
      if (a.sentAt < b.sentAt) return -1;
      if (a.sentAt > b.sentAt) return 1;
      return a.messageHash < b.messageHash ? -1 : a.messageHash > b.messageHash ? 1 : 0;
    });
    const ordered = order === 'desc' ? replies.reverse() : replies;
    return ordered.slice(0, limit);
  }

  /**
   * Phase 27.6: compute the set of currently-hidden target hashes for a
   * group, given a pre-fetched row pool. Mirrors `isHidden` logic
   * (per-target latest mod action wins, community-vote falls back to
   * quorum) but runs in a single pass — used by `listMessages` /
   * `listReplies` to avoid an isHidden round-trip per row.
   *
   * Community-vote quorum here uses the default heuristic:
   * `ceil(members / 2)`. The single-shot `tallyHideVotes` API offers an
   * `opts.quorum` override; the renderer path uses the default.
   */
  #computeHiddenSet(group: SocialGroupRecord, rows: MessageCacheRow[]): Set<string> {
    type ModAction = { kind: 'hide' | 'restore'; sentAt: string };
    const lastModByTarget = new Map<string, ModAction>();
    const votesByTarget = new Map<string, Set<string>>();
    const memberSet = new Set<string>([group.createdBy, ...group.moderatorPubkeys]);

    // Sort by (sentAt, hash) so "latest action wins" is deterministic even
    // when same-ms timestamps collide.
    const sorted = rows.slice().sort((a, b) => {
      if (a.sentAt < b.sentAt) return -1;
      if (a.sentAt > b.sentAt) return 1;
      return a.hash < b.hash ? -1 : a.hash > b.hash ? 1 : 0;
    });

    for (const r of sorted) {
      let parsed: { kind?: string; groupId?: string; targetHash?: string; senderInboxKey?: string } | null = null;
      try { parsed = JSON.parse(r.text); } catch { continue; }
      if (parsed === null) continue;
      if (parsed.groupId !== group.id || typeof parsed.targetHash !== 'string') continue;

      // Phase 28.3: prefer the inbox-key claim from the payload when present
      // (cross-peer mod recognition). Fall back to envelope senderPubkey for
      // legacy cache rows from pre-28.3 publishes.
      const author = parsed.senderInboxKey ?? r.senderPubkey;
      const isMod = memberSet.has(author);
      const target = parsed.targetHash;

      if (parsed.kind === 'social-hide') {
        // Phase 28.3: strict mod check, mirroring `isHidden`.
        if (!isMod) continue;
        const prev = lastModByTarget.get(target);
        if (prev === undefined || prev.sentAt <= r.sentAt) {
          lastModByTarget.set(target, { kind: 'hide', sentAt: r.sentAt });
        }
      } else if (parsed.kind === 'social-restore') {
        if (!isMod) continue;
        const prev = lastModByTarget.get(target);
        if (prev === undefined || prev.sentAt <= r.sentAt) {
          lastModByTarget.set(target, { kind: 'restore', sentAt: r.sentAt });
        }
      } else if (parsed.kind === 'social-hide-vote' && group.moderation === 'community-vote') {
        let set = votesByTarget.get(target);
        if (set === undefined) { set = new Set(); votesByTarget.set(target, set); }
        set.add(r.senderPubkey);
      }
    }

    const hidden = new Set<string>();
    for (const [target, action] of lastModByTarget) {
      if (action.kind === 'hide') hidden.add(target);
    }
    if (group.moderation === 'community-vote') {
      // Default quorum derived from currently-active members count is
      // expensive to compute here without an async call; for the
      // renderer-path heuristic, fall back to `max(1, ceil(memberSet/2))`
      // — close enough for v1, callers can use `tallyHideVotes` with a
      // custom quorum for definitive answers.
      const quorum = Math.max(1, Math.ceil(memberSet.size / 2));
      for (const [target, voters] of votesByTarget) {
        if (voters.size >= quorum && !lastModByTarget.has(target)) {
          hidden.add(target);
        }
      }
    }
    return hidden;
  }

  #decodePostPayload(text: string, expectedGroupId: string): SocialPostPayload | null {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && parsed.kind === 'social-post' && parsed.groupId === expectedGroupId) {
        return parsed as SocialPostPayload;
      }
    } catch {
      return null;
    }
    return null;
  }

  /**
   * Purge `message_cache` rows whose social-post payload's TTL has elapsed
   * relative to `sentAt`. Scoped to one group if `groupId` is provided;
   * otherwise sweeps every social group's underlying thread. Returns the
   * number of rows swept. Audits one `social.posts-swept` entry per group
   * touched (skipped when zero rows swept for that group).
   *
   * The underlying encrypted log retains the original envelopes; this only
   * drops the local decrypted cache. Peers may rehydrate from their own
   * cache (if not yet expired there) until propagation completes.
   */
  async sweepExpiredPosts(opts: { groupId?: string; now?: Date } = {}): Promise<{ swept: number }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const nowMs = (opts.now ?? new Date()).getTime();

    const groups = opts.groupId !== undefined
      ? [await db.social_groups.get(opts.groupId)].filter((g): g is import('../storage/schema').SocialGroupRecord => g !== undefined)
      : await db.social_groups.toArray();

    let totalSwept = 0;
    for (const group of groups) {
      const contextKey = `social:group:${group.id}`;
      const rows = await db.message_cache
        .where('[context+threadId]')
        .equals([contextKey, group.mlsGroupId])
        .toArray();

      const expiredHashes: string[] = [];
      for (const r of rows) {
        let plaintext: Uint8Array;
        try {
          plaintext = await ctx.storage.decryptForSession(r.ciphertext);
        } catch {
          continue;
        }
        const payload = this.#decodePostPayload(new TextDecoder().decode(plaintext), group.id);
        if (payload === null) continue;
        if (payload.ttlSec === undefined) continue;
        const expiresMs = new Date(r.sentAt).getTime() + payload.ttlSec * 1000;
        if (expiresMs <= nowMs) expiredHashes.push(r.hash);
      }

      if (expiredHashes.length === 0) continue;
      await db.message_cache.bulkDelete(expiredHashes);
      totalSwept += expiredHashes.length;
      await this.audit({
        action: 'social.posts-swept',
        context: `social:group:${group.id}`,
        details: { groupId: group.id, swept: expiredHashes.length },
      });
    }

    return { swept: totalSwept };
  }

  async markRead(groupId: string): Promise<void> {
    const ctx = this.#ctx;
    const group = await ctx.storage.db.social_groups.get(groupId);
    if (group === undefined) {
      throw new SocialValidationError(`markRead: unknown groupId ${groupId}`);
    }
    await this.#threads.markRead({ threadId: group.mlsGroupId });
    await this.audit({
      action: 'social.group-marked-read',
      context: `social:group:${groupId}`,
      details: { groupId },
    });
  }

  async listMyMentions(opts: { onlyUnread?: boolean; limit?: number } = {}): Promise<SocialMentionRecord[]> {
    const ctx = this.#ctx;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const rows = await ctx.storage.db.social_mentions
      .where('ownPubkey')
      .equals(inboxKey)
      .toArray();
    const filtered = opts.onlyUnread === true ? rows.filter((r) => r.readAt === undefined) : rows;
    filtered.sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : a.receivedAt > b.receivedAt ? -1 : 0));
    return opts.limit !== undefined ? filtered.slice(0, opts.limit) : filtered;
  }

  /**
   * Mark a single mention as read (sets `readAt`). Used by surfaces when
   * the user views a mention. Idempotent; calling on an already-read
   * mention bumps the timestamp.
   */
  async markMentionRead(mentionId: string): Promise<void> {
    const now = new Date().toISOString();
    await this.#ctx.storage.db.social_mentions.update(mentionId, { readAt: now });
  }

  /**
   * Scan `message_cache` for the given group (or all groups), decrypt each
   * post, and write a `social_mentions` row for any post whose
   * `mentionPubkeys` includes the local inbox key. Idempotent via composite
   * primary key `${messageHash}:${ownPubkey}`. Used by recipients to
   * hydrate cross-peer mentions; the publish path already self-mentions.
   *
   * Returns the number of NEW mention rows written (re-ingests are no-ops
   * but still rewrite the same row, so we filter pre-existing IDs).
   */
  async ingestIncomingMentions(opts: { groupId?: string } = {}): Promise<{ ingested: number }> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));

    const groups = opts.groupId !== undefined
      ? [await db.social_groups.get(opts.groupId)].filter((g): g is SocialGroupRecord => g !== undefined)
      : await db.social_groups.toArray();

    let ingested = 0;
    for (const group of groups) {
      const contextKey = `social:group:${group.id}`;
      const rows = await db.message_cache
        .where('[context+threadId]')
        .equals([contextKey, group.mlsGroupId])
        .toArray();

      for (const r of rows) {
        let plaintext: Uint8Array;
        try { plaintext = await ctx.storage.decryptForSession(r.ciphertext); }
        catch { continue; }
        const payload = this.#decodePostPayload(new TextDecoder().decode(plaintext), group.id);
        if (payload === null) continue;
        if (payload.mentionPubkeys === undefined || !payload.mentionPubkeys.includes(inboxKey)) continue;

        const id = mentionId(r.hash, inboxKey);
        const existing = await db.social_mentions.get(id);
        if (existing !== undefined) continue;  // already indexed

        const record: SocialMentionRecord = {
          id,
          ownPubkey: inboxKey,
          messageHash: r.hash,
          groupId: group.id,
          receivedAt: r.receivedAt,
        };
        await db.social_mentions.put(record);
        ingested += 1;
      }
    }

    if (ingested > 0) {
      await this.audit({
        action: 'social.mentions-ingested',
        context: opts.groupId !== undefined ? `social:group:${opts.groupId}` : 'social:all',
        details: { ingested, scope: opts.groupId ?? 'all' },
      });
    }
    return { ingested };
  }

  /**
   * Return the count of cached posts in the underlying thread whose
   * `sentAt` is strictly greater than the user's `read_state` pointer.
   * Excludes story posts and replies — matches `listMessages` default.
   */
  async getUnreadCount(groupId: string): Promise<number> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(groupId);
    if (group === undefined) return 0;

    const contextKey = `social:group:${groupId}`;
    const recordId = `${contextKey}:${group.mlsGroupId}`;
    const rs = await db.read_state.get(recordId);
    const cutoff = rs?.lastReadMessageAt ?? '';

    const rows = await db.message_cache
      .where('[context+threadId]')
      .equals([contextKey, group.mlsGroupId])
      .toArray();

    let count = 0;
    for (const r of rows) {
      if (r.sentAt <= cutoff) continue;
      let plaintext: Uint8Array;
      try { plaintext = await ctx.storage.decryptForSession(r.ciphertext); }
      catch { continue; }
      const payload = this.#decodePostPayload(new TextDecoder().decode(plaintext), groupId);
      if (payload === null) continue;
      if (payload.parentHash !== undefined) continue;  // top-level only
      if (payload.story === true) continue;            // stories live elsewhere
      count += 1;
    }
    return count;
  }

  /** Return `{groupId: unreadCount}` for every active subscription of the user. */
  async getUnreadCountsByGroup(): Promise<Record<string, number>> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const subs = await db.social_subscriptions.where('ownPubkey').equals(inboxKey).toArray();
    const active = subs.filter((s) => s.leftAt === undefined);
    const out: Record<string, number> = {};
    for (const s of active) {
      out[s.groupId] = await this.getUnreadCount(s.groupId);
    }
    return out;
  }

  /**
   * Full-text search over locally-cached posts. Decrypts each cache row,
   * case-insensitive substring match against `payload.text`. Restricted to
   * groups the user is a member of (or to `opts.groupId` if specified).
   * Result ordered newest-first.
   */
  async search(opts: SearchOptions): Promise<SearchHit[]> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const query = opts.query.trim().toLowerCase();
    if (query.length === 0) return [];
    const limit = opts.limit ?? 50;

    let groupRecords: SocialGroupRecord[];
    if (opts.groupId !== undefined) {
      const g = await db.social_groups.get(opts.groupId);
      groupRecords = g !== undefined ? [g] : [];
    } else {
      const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
      const subs = await db.social_subscriptions.where('ownPubkey').equals(inboxKey).toArray();
      const myGroupIds = new Set(subs.filter((s) => s.leftAt === undefined).map((s) => s.groupId));
      groupRecords = (await db.social_groups.toArray()).filter((g) => myGroupIds.has(g.id));
    }

    const hits: SearchHit[] = [];
    for (const group of groupRecords) {
      const contextKey = `social:group:${group.id}`;
      const rows = await db.message_cache
        .where('[context+threadId]')
        .equals([contextKey, group.mlsGroupId])
        .toArray();
      for (const r of rows) {
        let plaintext: Uint8Array;
        try { plaintext = await ctx.storage.decryptForSession(r.ciphertext); }
        catch { continue; }
        const payload = this.#decodePostPayload(new TextDecoder().decode(plaintext), group.id);
        if (payload === null) continue;
        const text = payload.text ?? '';
        const lower = text.toLowerCase();
        const idx = lower.indexOf(query);
        if (idx < 0) continue;
        // Build a snippet — 30 chars before + match + 30 chars after.
        const start = Math.max(0, idx - 30);
        const end = Math.min(text.length, idx + query.length + 30);
        const snippet = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
        hits.push({
          groupId: group.id,
          groupName: group.name,
          messageHash: r.hash,
          snippet,
          matchedAt: r.sentAt,
        });
      }
    }
    hits.sort((a, b) => (a.matchedAt < b.matchedAt ? 1 : a.matchedAt > b.matchedAt ? -1 : 0));
    return hits.slice(0, limit);
  }

  // ─── Phase 28.2: sealed-sender verification ─────────────────────────────

  /**
   * Verify the Semaphore proof attached to a sealed-sender post. Returns
   * `{ok}` when the proof validates against the named neighborhood's
   * current commitment root and binds to the post's content hash.
   * Posts without `sealedSenderProof` return `{ok: true, reason: 'no-proof'}`.
   */
  async verifySealedSender(post: {
    payload: SocialPostPayload;
  }): Promise<{ ok: boolean; reason?: string }> {
    const sp = post.payload.sealedSenderProof;
    if (sp === undefined) return { ok: true, reason: 'no-proof' };
    // Re-compute the same content-binding hash the sender used. To do so
    // deterministically we strip the proof field itself before hashing.
    const stripped: SocialPostPayload = { ...post.payload };
    delete stripped.sealedSenderProof;
    const { blake3 } = await import('@noble/hashes/blake3');
    const messageBinding = bytesToHex(blake3(new TextEncoder().encode(JSON.stringify(stripped)))).slice(0, 32);
    const { MembershipManager } = await import('../membership/MembershipManager');
    const mm = new MembershipManager(this.#ctx);
    return mm.verifyProofAgainst({
      neighborhoodId: sp.neighborhoodId,
      proof: sp.proof,
      externalNullifier: `social-sealed:${post.payload.groupId}`,
      message: messageBinding,
    });
  }

  // ─── Federation hooks (Phase 27.7) ──────────────────────────────────────

  /**
   * Phase 27.7: attach federation links to a group. Moderator-only.
   * Subsequent `publish()` calls auto-forward posts across each active
   * link via `FederationManager.forwardSocialPost` (fire-and-forget).
   * Returns the merged set of `federationLinkIds` on the group.
   */
  async federateGroup(groupId: string, federationLinkIds: string[]): Promise<string[]> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(groupId);
    if (group === undefined) throw new SocialValidationError(`federateGroup: unknown groupId ${groupId}`);
    if (group.archivedAt !== undefined) throw new SocialAuthError('group-archived');
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const isMod = group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
    if (!isMod) throw new SocialAuthError('not-a-moderator');

    const merged = Array.from(new Set([...group.federationLinkIds, ...federationLinkIds]));
    await db.social_groups.update(groupId, { federationLinkIds: merged });
    await this.audit({
      action: 'social.group-federated',
      context: `social:group:${groupId}`,
      details: { groupId, added: federationLinkIds, mergedCount: merged.length },
    });
    return merged;
  }

  async unfederateGroup(groupId: string, federationLinkIds: string[]): Promise<void> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;
    const group = await db.social_groups.get(groupId);
    if (group === undefined) throw new SocialValidationError(`unfederateGroup: unknown groupId ${groupId}`);
    const inboxKey = bytesToHex(await ctx.identity.pubkey(SOCIAL_INBOX_CONTEXT));
    const isMod = group.createdBy === inboxKey || group.moderatorPubkeys.includes(inboxKey);
    if (!isMod) throw new SocialAuthError('not-a-moderator');

    const removeSet = new Set(federationLinkIds);
    const remaining = group.federationLinkIds.filter((id) => !removeSet.has(id));
    await db.social_groups.update(groupId, { federationLinkIds: remaining });
    await this.audit({
      action: 'social.group-unfederated',
      context: `social:group:${groupId}`,
      details: { groupId, removed: federationLinkIds, remainingCount: remaining.length },
    });
  }

  // ─── Internal: audit ────────────────────────────────────────────────────

  private async audit(input: {
    action: string;
    context: string;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.#ctx;
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: 'social' as const,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await ctx.identity.sign(new TextEncoder().encode(canonical), SOCIAL_INBOX_CONTEXT);
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'social',
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try {
      await ctx.storage.db.audit_log.add(record);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`SocialGroupManager.audit: ${errMsg(err)}`);
    }
  }
}

// ─── per-AppContext singleton ──────────────────────────────────────────────

const MANAGER_BY_CTX = new WeakMap<AppContext, SocialGroupManager>();

export function getSocialGroupManager(ctx: AppContext): SocialGroupManager {
  let mgr = MANAGER_BY_CTX.get(ctx);
  if (mgr === undefined) {
    mgr = new SocialGroupManager(ctx);
    MANAGER_BY_CTX.set(ctx, mgr);
  }
  return mgr;
}
